import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ActivityFeed from './ActivityFeed';
import LoadingSpinner from '../../components/LoadingSpinner';
import WelcomeModal from './WelcomeModal';

// ICONS
import { ReactComponent as TickSuccessIcon } from '../../assets/svg/dashboard-complete.svg';
import { ReactComponent as TickAlertIcon } from '../../assets/svg/dashboard-alert.svg';

// TYPES
import { DomainProfileType, DashboardSectionSummariesSections, DashboardActivityFeedActivity } from '../../api/models';
import GlobalModal from '../../components/GlobalModal';
import DownloadContactListModal from './DownloadContactListModal';

const Dashboard: React.FC = () => {
	const history = useHistory();
	const dispatch = useAppDispatch();

	const [mounted, setMounted] = React.useState<boolean>(false);
	const count = React.useRef(0);

	React.useEffect(() => {
		if (mounted) {
			getDashboardDetail();
		}
	}, [mounted]);

	// Get userName from Redux store
	const userName = useAppSelector((state) => state?.user.firstName);

	const accountsEmail = useAppSelector((store) => store.lists.accountsEmail);

	const userProfiles = useAppSelector((state) => state?.user.profiles);

	// Check if user is a Super Admin
	const isSuperAdministrator = useAppSelector(
		(state) => state?.user.activeProfileType === DomainProfileType.SuperAdministrator
	);

	// Check if the user has completed the walkthrough
	const isWalkThroughCompleted = useAppSelector((state) => state?.user.isWalkThroughCompleted);

	// Check to see if the Organisation is active
	const isActiveOrganisation = useAppSelector((state) => state.user.isOrganisationActive);

	// Check to see if the Organisation is pending
	const isOrganisationPending = useAppSelector((state) => state.user.organisationStatus === 'Pending');

	// Check to see if the OrganisationID is active
	const activeOrganisationId = useAppSelector((state) => state.user.activeOrganisationId);

	// Check to see if use is Org Admin
	const isOrgAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Contact email
	const contactEmail = useAppSelector((state) => state.lists.contactEmail);

	// Error and Loading state
	const [dashboardFormErrorMessage, setDashboardFormErrorMessage] = React.useState<string>('');

	const [activityFeedFormErrorMessage, setActivityFeedFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Dashboard Data state
	const [dashboardItems, setDashboardItems] = React.useState<DashboardSectionSummariesSections>({});

	const getDashboardItems = async () => {
		if (userProfiles && userProfiles.length >= 1) {
			if (activeOrganisationId) {
				const response = await apibridge.getDashboardItems();

				if (response.isError) {
					setIsLoading(false);

					setDashboardFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else {
					const { result = {} } = response;

					setIsLoading(false);

					setDashboardItems(result);
				}
			} else {
				const response = await apibridge.doSwitchOrganisation({ profileId: userProfiles[0].id });

				if (response.isError) {
					setIsLoading(false);

					setDashboardFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else {
					setIsLoading(false);

					dispatch(UserSlice.actions.forceUpdate({}));
				}
			}
		} else if (mounted) {
			history.push('/landingDashboard');
		}
	};

	// Set up Activity Feed Data state
	const [activityFeedData, setActivityFeedData] = React.useState<DashboardActivityFeedActivity[]>([]);

	const getActivityFeed = async () => {
		const response = await apibridge.getActivityFeed();

		if (response.isError) {
			setActivityFeedFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;
			setActivityFeedData(result);
		}
	};

	// Combine 2 API calls to avoid spinner flashing
	const getDashboardDetail = async () => {
		setIsLoading(true);

		await getDashboardItems();

		await getActivityFeed();

		setIsLoading(false);
	};

	React.useEffect(() => {
		if (isSuperAdministrator) {
			history.push('/companies');
		}
		setMounted(true);
	}, []);

	const {
		profile,

		staff,
		leadershipTeam,
		families,
		visitors,

		training,
		safetyChecklists,
		policies,

		complaints,
		feedback,
		incidentReports,

		incompleteProfileCount,

		canViewActivityFeed,
		canDownloadContactList
	} = dashboardItems;

	// Download Contact List
	const [showDownloadContactListModal, setShowDownloadContactListModal] = React.useState<boolean>(false);

	return (
		<div className="dashboard-content">
			{isOrganisationPending && (
				<div className="payment-error">
					Payment for your organisation’s ChildAware account has not been completed. If you have made a payment please
					contact ChildAware at <a href={`mailto:${accountsEmail || ''}`}>accounts@childaware.com</a> for assistance.
					Otherwise, please make a <Link to={`/register-organisation-payment?id=${activeOrganisationId}`}>payment</Link>{' '}
					to complete this process.
				</div>
			)}

			{!!dashboardFormErrorMessage && !isOrganisationPending ? (
				<div className="form-validation-error">{dashboardFormErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<section className="dashboard-info">
									{profile ? (
										<article className="dashboard-info-block">
											{profile.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Your profile</h4>

														<div className="card-info staff-card">
															{profile.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{profile.counts?.expiredCertifications && profile.counts.expiredCertifications > 0 ? (
																		<Link className="info-content" to="/my-account">
																			<span className="info-count">{profile.counts.expiredCertifications}</span>

																			<p className="info-text">
																				document{profile.counts.expiredCertifications > 1 ? 's' : null} require
																				{profile.counts.expiredCertifications > 1 ? null : 's'} your attention
																			</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link to="/my-account" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}
										</article>
									) : null}

									{incompleteProfileCount ? (
										<article className="dashboard-info-block">
											{incompleteProfileCount.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Incomplete Profiles</h4>

														<div className="card-info staff-card">
															<Link className="info-content" to="/incomplete-profiles">
																{incompleteProfileCount.isUpToDate ? (
																	<div className="info-content complete-status">
																		<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																	</div>
																) : (
																	<div className="d-flex gap-2">
																		<TickAlertIcon />
																		<div className="d-flex align-items-center justify-content-start">
																			<span className="info-count">{incompleteProfileCount.counts?.members}</span>

																			<p className="info-text">
																				member
																				{incompleteProfileCount.counts?.members &&
																				incompleteProfileCount.counts.members > 1
																					? 's '
																					: ' '}
																				- Action required
																			</p>
																		</div>
																	</div>
																)}
															</Link>
														</div>
													</div>

													<div className="card-right">
														<Link to="/incomplete-profiles" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}
										</article>
									) : null}

									{staff || leadershipTeam || families ? (
										<article className="dashboard-info-block">
											{staff && staff.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Staff or Volunteer</h4>

														<div className="card-info staff-card">
															{staff.counts?.members && staff.counts?.members > 0 ? (
																<Link className="info-content" to="/staff">
																	<span className="info-count">{staff.counts?.members}</span>

																	<p className="info-text">
																		{staff.counts?.members && staff.counts?.members === 1 ? 'member' : 'members'}
																	</p>
																</Link>
															) : null}

															{staff.counts?.joinRequests && staff.counts?.joinRequests > 0 ? (
																<Link className="info-content" to="/requests-to-join">
																	<span className="info-count">{staff.counts.joinRequests}</span>

																	<p className="info-text">
																		new join request
																		{staff.counts?.joinRequests && staff.counts?.joinRequests > 1 ? 's' : null}
																	</p>
																</Link>
															) : null}

															{staff.counts?.incompleteProfiles && staff.counts?.incompleteProfiles > 0 ? (
																<Link className="info-content" to="/staff">
																	<span className="info-count">{staff.counts?.incompleteProfiles}</span>

																	<p className="info-text">
																		incomplete profile
																		{staff.counts?.incompleteProfiles && staff.counts?.incompleteProfiles > 1
																			? 's'
																			: null}
																	</p>
																</Link>
															) : null}
														</div>
													</div>

													<div className="card-right">
														<Link to="/staff" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}

											{leadershipTeam && leadershipTeam.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Leadership Team</h4>

														<div className="card-info">
															{leadershipTeam.counts?.members && leadershipTeam.counts?.members > 0 ? (
																<Link className="info-content" to="/leadershipteam">
																	<span className="info-count">{leadershipTeam.counts?.members}</span>

																	<p className="info-text">
																		{leadershipTeam.counts?.members && leadershipTeam.counts?.members === 1
																			? 'member'
																			: 'members'}
																	</p>
																</Link>
															) : null}
														</div>
													</div>

													<div className="card-right">
														<Link to="/leadershipteam" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}

											{families && families.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Families</h4>

														<div className="card-info">
															{families.counts?.members && families.counts?.members > 0 ? (
																<Link className="info-content" to="/families">
																	<span className="info-count">{families.counts?.members}</span>

																	<p className="info-text">
																		{families.counts?.members && families.counts?.members === 1 ? 'member' : 'members'}
																	</p>
																</Link>
															) : null}

															{families.counts?.joinRequests && families.counts?.joinRequests > 0 ? (
																<Link className="info-content" to="/requests-to-join">
																	<span className="info-count">{families.counts?.joinRequests}</span>

																	<p className="info-text">join requests</p>
																</Link>
															) : null}

															{families.counts?.incompleteProfiles && families.counts?.incompleteProfiles > 0 ? (
																<Link className="info-content" to="/families">
																	<span className="info-count">{families.counts?.incompleteProfiles}</span>

																	<p className="info-text">incomplete profiles</p>
																</Link>
															) : null}
														</div>
													</div>

													<div className="card-right">
														<Link to="/families" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}

											{visitors && visitors.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Visitors</h4>

														<div className="card-info">
															{visitors.counts?.members && visitors.counts?.members > 0 ? (
																<Link className="info-content" to="/visitors">
																	<span className="info-count">{visitors.counts?.members}</span>

																	<p className="info-text">
																		{visitors.counts?.members && visitors.counts?.members === 1 ? 'member' : 'members'}
																	</p>
																</Link>
															) : null}

															{visitors.counts?.joinRequests && visitors.counts?.joinRequests > 0 ? (
																<Link className="info-content" to="/requests-to-join">
																	<span className="info-count">{visitors.counts?.joinRequests}</span>

																	<p className="info-text">
																		join request{visitors.counts?.joinRequests > 1 ? 's' : null}
																	</p>
																</Link>
															) : null}

															{visitors.counts?.incompleteProfiles && visitors.counts?.incompleteProfiles > 0 ? (
																<Link className="info-content" to="/visitors">
																	<span className="info-count">{visitors.counts?.incompleteProfiles}</span>

																	<p className="info-text">
																		incomplete profile{visitors.counts?.incompleteProfiles > 1 ? 's' : null}
																	</p>
																</Link>
															) : null}
														</div>
													</div>

													<div className="card-right">
														<Link to="/visitors" className="card-link btn-text">
															View
														</Link>
													</div>
												</div>
											)}
										</article>
									) : null}

									{training || safetyChecklists || policies ? (
										<article className="dashboard-info-block">
											{policies && policies.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Policies</h4>

														<div className="card-info">
															{policies.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{policies.counts?.expired && policies.counts?.expired > 0 ? (
																		<Link className="info-content" to="/policies/admin-view">
																			<span className="info-count">{policies.counts?.expired}</span>

																			<p className="info-text">expired</p>
																		</Link>
																	) : null}

																	{policies.counts?.forOthersToAcknowledge &&
																	policies.counts?.forOthersToAcknowledge > 0 ? (
																		<Link className="info-content" to="/policies/admin-view">
																			<span className="info-count">{policies.counts?.forOthersToAcknowledge}</span>

																			<p className="info-text">for others to acknowledge</p>
																		</Link>
																	) : null}

																	{policies.counts?.forYouToAcknowledge && policies.counts?.forYouToAcknowledge > 0 ? (
																		<Link className="info-content" to="/policies/user-view">
																			<span className="info-count">{policies.counts?.forYouToAcknowledge}</span>

																			<p className="info-text">for you to acknowledge</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/policies/admin-view' : '/policies/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}

											{training && training.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Training</h4>

														<div className="card-info">
															{training.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{training.counts?.assigned && training.counts?.assigned > 0 ? (
																		<Link className="info-content" to="/training/admin-view">
																			<span className="info-count">{training.counts?.assigned}</span>

																			<p className="info-text">assigned</p>
																		</Link>
																	) : null}

																	{training.counts?.overdueByOthers && training.counts?.overdueByOthers > 0 ? (
																		<Link className="info-content" to="/training/admin-view">
																			<span className="info-count">{training.counts?.overdueByOthers}</span>

																			<p className="info-text">overdue by others</p>
																		</Link>
																	) : null}

																	{training.counts?.forYouToComplete && training.counts?.forYouToComplete > 0 ? (
																		<Link className="info-content" to="/training/user-view">
																			<span className="info-count">{training.counts?.forYouToComplete}</span>

																			<p className="info-text">for you to complete</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/training/admin-view' : '/training/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}

											{safetyChecklists && safetyChecklists.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Safety Checklists</h4>

														<div className="card-info">
															{safetyChecklists.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{safetyChecklists.counts?.assigned && safetyChecklists.counts?.assigned > 0 ? (
																		<Link className="info-content" to="/safety-checklists/admin-view">
																			<span className="info-count">{safetyChecklists.counts?.assigned}</span>

																			<p className="info-text">assigned</p>
																		</Link>
																	) : null}

																	{safetyChecklists.counts?.overdueByOthers &&
																	safetyChecklists.counts?.overdueByOthers > 0 ? (
																		<Link className="info-content" to="/safety-checklists/admin-view">
																			<span className="info-count">{safetyChecklists.counts?.overdueByOthers}</span>

																			<p className="info-text">overdue</p>
																		</Link>
																	) : null}

																	{safetyChecklists.counts?.forYouToComplete &&
																	safetyChecklists.counts?.forYouToComplete > 0 ? (
																		<Link className="info-content" to="/safety-checklists/user-view">
																			<span className="info-count">{safetyChecklists.counts?.forYouToComplete}</span>

																			<p className="info-text">for you to complete</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/safety-checklists/admin-view' : '/safety-checklists/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}
										</article>
									) : null}

									{complaints || feedback || incidentReports ? (
										<article className="dashboard-info-block">
											{feedback && feedback.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Feedback</h4>

														<div className="card-info">
															{feedback.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{feedback.counts?.newlySubmitted && feedback.counts?.newlySubmitted > 0 ? (
																		<Link className="info-content" to="/feedback/admin-view">
																			<span className="info-count">{feedback.counts?.newlySubmitted}</span>

																			<p className="info-text">newly submitted</p>
																		</Link>
																	) : null}

																	{feedback.counts?.inProgress && feedback.counts?.inProgress > 0 ? (
																		<Link className="info-content" to="/feedback/admin-view">
																			<span className="info-count">{feedback.counts?.inProgress}</span>

																			<p className="info-text">in progress</p>
																		</Link>
																	) : null}

																	{feedback.counts?.submittedByYou && feedback.counts?.submittedByYou > 0 ? (
																		<Link className="info-content" to="/feedback/user-view">
																			<span className="info-count">{feedback.counts?.submittedByYou}</span>

																			<p className="info-text">submitted by you</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/feedback/admin-view' : '/feedback/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}

											{complaints && complaints.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Complaints</h4>

														<div className="card-info">
															{complaints.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{complaints.counts?.newlySubmitted && complaints.counts?.newlySubmitted > 0 ? (
																		<Link className="info-content" to="/complaints/admin-view">
																			<span className="info-count">{complaints.counts?.newlySubmitted}</span>

																			<p className="info-text">newly submitted</p>
																		</Link>
																	) : null}

																	{complaints.counts?.inProgress && complaints.counts?.inProgress > 0 ? (
																		<Link className="info-content" to="/complaints/admin-view">
																			<span className="info-count">{complaints.counts?.inProgress}</span>

																			<p className="info-text">in progress</p>
																		</Link>
																	) : null}

																	{complaints.counts?.submittedByYou && complaints.counts?.submittedByYou > 0 ? (
																		<Link className="info-content" to="/complaints/user-view">
																			<span className="info-count">{complaints.counts?.submittedByYou}</span>

																			<p className="info-text">submitted by you</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/complaints/admin-view' : '/complaints/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}

											{incidentReports && incidentReports.canRead && (
												<div className="dashboard-info-card">
													<div className="card-left">
														<h4 className="card-title">Incident Reports</h4>

														<div className="card-info">
															{incidentReports.isUpToDate ? (
																<div className="info-content complete-status">
																	<TickSuccessIcon /> <p className="info-text green mleft-6">up to date</p>
																</div>
															) : (
																<>
																	{incidentReports.counts?.forOthersToAcknowledge &&
																	incidentReports.counts?.forOthersToAcknowledge > 0 ? (
																		<Link className="info-content" to="/incident-reports/admin-view">
																			<span className="info-count">
																				{incidentReports.counts?.forOthersToAcknowledge}
																			</span>

																			<p className="info-text">in progress</p>
																		</Link>
																	) : null}

																	{incidentReports.counts?.forYouToAcknowledge &&
																	incidentReports.counts?.forYouToAcknowledge > 0 ? (
																		<Link className="info-content" to="/incident-reports/user-view">
																			<span className="info-count">{incidentReports.counts?.forYouToAcknowledge}</span>

																			<p className="info-text">for you to acknowledge</p>
																		</Link>
																	) : null}
																</>
															)}
														</div>
													</div>

													<div className="card-right">
														<Link
															to={isOrgAdmin ? '/incident-reports/admin-view' : '/incident-reports/user-view'}
															className="card-link btn-text"
														>
															View
														</Link>
													</div>
												</div>
											)}
										</article>
									) : null}

									{canDownloadContactList && (
										<article className="dashboard-info-block">
											<div className="dashboard-info-card py-3">
												<div className="card-left">
													<h4 className="card-title">Contact List</h4>
												</div>

												<div className="card-right">
													<button
														className="btn card-link btn-text"
														onClick={() => setShowDownloadContactListModal(true)}
													>
														Download
													</button>
												</div>
											</div>
										</article>
									)}
								</section>

								{canViewActivityFeed && (
									<ActivityFeed
										activityFeedData={activityFeedData}
										getActivityFeed={getActivityFeed}
										isLoading={isLoading}
										activityFeedFormErrorMessage={activityFeedFormErrorMessage}
									></ActivityFeed>
								)}

								<WelcomeModal
									contactEmail={contactEmail}
									isOrgAdmin={isOrgAdmin || false}
									userName={userName || ''}
									isOpen={!isWalkThroughCompleted || false}
								/>

								<GlobalModal
									isOpen={showDownloadContactListModal}
									setIsOpen={setShowDownloadContactListModal}
									titleText="Contact List"
								>
									<DownloadContactListModal />
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Dashboard;
